import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { adminService, SubmissionStatus } from '../services/admin';

export const adminKeys = {
  all: ['admin'] as const,
  submissions: (status: SubmissionStatus) =>
    [...adminKeys.all, 'submissions', status] as const,
  deadLetters: () => [...adminKeys.all, 'deadLetters'] as const,
  deadLetterStats: () => [...adminKeys.all, 'deadLetterStats'] as const,
  users: (search: string) => [...adminKeys.all, 'users', search] as const,
  costs: (days: number) => [...adminKeys.all, 'costs', days] as const,
};

// ── Marketplace moderation ─────────────────────────────────────

export function useSubmissions(status: SubmissionStatus = 'PENDING') {
  return useQuery({
    queryKey: adminKeys.submissions(status),
    queryFn: () => adminService.listSubmissions(status),
  });
}

export function useApproveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => adminService.approveTemplate(templateId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...adminKeys.all, 'submissions'] }),
  });
}

export function useRejectTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, reason }: { templateId: string; reason: string }) =>
      adminService.rejectTemplate(templateId, reason),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...adminKeys.all, 'submissions'] }),
  });
}

// ── Dead-letter queue ──────────────────────────────────────────

export function useDeadLetters() {
  return useQuery({
    queryKey: adminKeys.deadLetters(),
    queryFn: () => adminService.listDeadLetters(),
  });
}

export function useDeadLetterStats() {
  return useQuery({
    queryKey: adminKeys.deadLetterStats(),
    queryFn: () => adminService.deadLetterStats(),
  });
}

export function useReplayDeadLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => adminService.replayDeadLetter(entryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.deadLetters() });
      qc.invalidateQueries({ queryKey: adminKeys.deadLetterStats() });
    },
  });
}

export function useDiscardDeadLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => adminService.discardDeadLetter(entryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.deadLetters() });
      qc.invalidateQueries({ queryKey: adminKeys.deadLetterStats() });
    },
  });
}

// ── User management ────────────────────────────────────────────

export function useAdminUsers(search: string) {
  return useQuery({
    queryKey: adminKeys.users(search),
    queryFn: () => adminService.listUsers(search),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: { role?: string; status?: string; isComped?: boolean };
    }) => adminService.updateUser(userId, data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...adminKeys.all, 'users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminService.deleteUser(userId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...adminKeys.all, 'users'] }),
  });
}


// ── Platform costs ─────────────────────────────────────────────

/** Platform-wide LLM spend across every user (admin only). */
export function useAdminCosts(days = 30) {
  return useQuery({
    queryKey: adminKeys.costs(days),
    queryFn: () => adminService.platformCosts(days),
    // Without this, switching 7/30/90 unmounts the whole panel — including the
    // buttons the user just clicked — because the new key has no cache.
    placeholderData: keepPreviousData,
  });
}
